import { Injectable } from "@angular/core";
import { BehaviorSubject, from, fromEvent, map, merge, Observable, tap } from "rxjs";
import { SavedSearchModel } from "./saved-search.model";

@Injectable({
    providedIn: "root"
})
export class SavedSearchesService {
    private LOCAL_STORAGE_KEY = "savedSearches";

    public savedSearches$: BehaviorSubject<Array<SavedSearchModel>>;
    public isSearchSaved$: Observable<{ saved: boolean }>;

    public constructor() {
        const serialized = window.localStorage.getItem(this.LOCAL_STORAGE_KEY);
        const savedSearches = !!serialized ? JSON.parse(serialized) as Array<SavedSearchModel> : [];
        this.savedSearches$ = new BehaviorSubject<Array<SavedSearchModel>>(savedSearches);

        fromEvent(window, "pcg.querystringchanged")
            .subscribe(() => this.savedSearches$.next([...this.savedSearches$.value]));

        this.isSearchSaved$ = this.savedSearches$.pipe(
            map(currentItems => {
                return { saved: currentItems.some(s => s.url === window.location.href) };
            })
        );

        this.savedSearches$.subscribe(this.saveToLocalStorage);
    }

    public create(savedSearch: SavedSearchModel): void {
        savedSearch.id = this.generateId();
        const currentValues = this.savedSearches$.value;
        currentValues.push(savedSearch);

        this.savedSearches$.next(currentValues);
    }

    public update(savedSearch: SavedSearchModel): void {
        const currentValues = this.savedSearches$.value;
        const updatedValue = currentValues.find(s => s.id === savedSearch.id);
        if (!updatedValue) {
            this.create(savedSearch);
            return;
        }

        updatedValue.name = savedSearch.name;
        updatedValue.url = savedSearch.url;
        updatedValue.description = savedSearch.description;

        this.savedSearches$.next(currentValues);
    }

    public delete(savedSearch: SavedSearchModel): void {
        let currentValues = this.savedSearches$.value;
        currentValues = currentValues.filter(s => s.id !== savedSearch.id);

        this.savedSearches$.next(currentValues);
    }

    private generateId: () => string = () =>
        Date.now().toString(36) + Math.random().toString(36).substr(2);

    private saveToLocalStorage: (currentItems: Array<SavedSearchModel>) => void =
        (currentItems: Array<SavedSearchModel>) => window.localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(currentItems));
}
